import React, { useMemo } from "react";
import { ADVANCED_FILTER_ALL } from "../../shared/utils/advancedFilter";

/**
 * Stacked advanced filter fields (list filter slide-over).
 *
 * Field definitions:
 * - { id, label, type: "text", placeholder? }
 * - { id, label, type: "select", options: [{ value, label }], allLabel?: string }
 * - { id, label, type: "number" | "percent" } — min/max range
 * - { id, label, type: "date" } — from/to range
 */
export default function AdvancedFilterFieldsForm({
  fields = [],
  values = {},
  onFieldChange,
  disabled = false,
}) {
  const normalizedFields = useMemo(
    () => (Array.isArray(fields) ? fields.filter(Boolean) : []),
    [fields]
  );

  if (normalizedFields.length === 0) return null;

  return (
    <div className="advanced-filter-fields-form advanced-filter-fields-form--stacked">
      {normalizedFields.map((f) => {
        if (!f?.id) return null;

        if (f.type === "select") {
          const options = Array.isArray(f.options) ? f.options.filter(Boolean) : [];
          const value = values[f.id] ?? ADVANCED_FILTER_ALL;
          const allLabel = f.allLabel || `All ${f.label || f.id}`;

          return (
            <div key={f.id} className="advanced-filter-fields-form__stacked-field">
              <label className="advanced-filter-fields-form__stacked-label">{f.label || f.id}</label>
              <select
                value={value}
                onChange={(e) => onFieldChange(f.id, e.target.value)}
                disabled={disabled}
                className="input"
                aria-label={f.label || f.id}
              >
                <option value={ADVANCED_FILTER_ALL}>{allLabel}</option>
                {options.map((opt) => (
                  <option key={`${f.id}:${String(opt.value)}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        if (f.type === "number" || f.type === "percent" || f.type === "date") {
          return (
            <div key={f.id} className="advanced-filter-fields-form__stacked-field">
              <label className="advanced-filter-fields-form__stacked-label">{f.label || f.id}</label>
              <RangeFilterControl
                field={f}
                values={values}
                onFieldChange={onFieldChange}
                disabled={disabled}
              />
            </div>
          );
        }

        if (f.type === "text") {
          const value = values[f.id] ?? "";
          return (
            <div key={f.id} className="advanced-filter-fields-form__stacked-field">
              <label className="advanced-filter-fields-form__stacked-label">{f.label || f.id}</label>
              <input
                type="text"
                value={value}
                onChange={(e) => onFieldChange(f.id, e.target.value)}
                disabled={disabled}
                className="input"
                placeholder={f.placeholder || "Type keywords..."}
                aria-label={f.label || f.id}
              />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

function RangeFilterControl({ field, values, onFieldChange, disabled }) {
  const isDate = field.type === "date";
  const current = values[field.id] && typeof values[field.id] === "object" ? values[field.id] : {};
  const leftKey = isDate ? "from" : "min";
  const rightKey = isDate ? "to" : "max";
  const leftLabel = isDate ? "From" : field.type === "percent" ? "Min %" : "Min";
  const rightLabel = isDate ? "To" : field.type === "percent" ? "Max %" : "Max";

  function update(key, next) {
    onFieldChange(field.id, { ...current, [key]: next });
  }

  return (
    <div className="advanced-filter-fields-form__range">
      <label className="advanced-filter-fields-form__range-field">
        <span className="advanced-filter-fields-form__range-label">{leftLabel}</span>
        <input
          type={isDate ? "date" : "number"}
          step={isDate ? undefined : "any"}
          value={current[leftKey] ?? ""}
          onChange={(e) => update(leftKey, e.target.value)}
          disabled={disabled}
          className="input"
          aria-label={`${field.label || field.id} ${leftLabel}`}
        />
      </label>
      <label className="advanced-filter-fields-form__range-field">
        <span className="advanced-filter-fields-form__range-label">{rightLabel}</span>
        <input
          type={isDate ? "date" : "number"}
          step={isDate ? undefined : "any"}
          value={current[rightKey] ?? ""}
          onChange={(e) => update(rightKey, e.target.value)}
          disabled={disabled}
          className="input"
          aria-label={`${field.label || field.id} ${rightLabel}`}
        />
      </label>
    </div>
  );
}
